import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const body = (res as { data?: unknown })?.data ?? res;
  return (body as { data?: T })?.data ?? (body as T);
}

export interface AssetDto {
  id: string;
  company_id: string;
  source_item_id?: string | null;
  source_grn_id?: string | null;
  source_grn_item_id?: string | null;
  asset_code: string;
  asset_name: string;
  asset_category: string | null;
  asset_subcategory: string | null;
  asset_group: string | null;
  asset_group_id?: string | null;
  serial_number: string | null;
  barcode: string | null;
  qr_code: string | null;
  manufacturer: string | null;
  model: string | null;
  specification: string | null;
  purchase_date: string | null;
  capitalization_date: string | null;
  capitalization_journal_voucher_id?: string | null;
  purchase_value: number | null;
  residual_value: number | null;
  useful_life_months: number | null;
  depreciation_method: string | null;
  current_book_value: number | null;
  status: string;
  warehouse_id: string | null;
  site: string | null;
  building: string | null;
  room: string | null;
  employee_id: string | null;
  department_id: string | null;
  supplier_id: string | null;
  warranty_expiry: string | null;
  remarks: string | null;
  /** API Q1 — used for conformity / process evidence */
  is_measuring_equipment?: boolean;
  /** CompanyOwned | CustomerOwned | EmployeeOwned | ThirdParty */
  equipment_ownership?: string | null;
  /** Control of TMMDE — LABEL | TAG | ENGRAVED */
  identification_method?: string | null;
  calibration_status?: string | null;
  calibration_due_date?: string | null;
  created_at?: string;
  updated_at?: string;
  last_verified_at?: string | null;
}

export interface GrnAssetLineSuggestion {
  grn_item_id: string;
  line_number: number;
  item_id: string | null;
  item_code: string | null;
  item_name: string | null;
  is_fixed_asset: boolean;
  track_serial_number: boolean;
  accepted_units: number;
  assets_created: number;
  remaining_units: number;
  suggested_serials: string[];
  unit_purchase_value: number | null;
}

export interface GrnAssetSuggestionsResult {
  grn_id: string;
  grn_number: string;
  grn_status: string;
  can_create_assets: boolean;
  require_serial_per_unit: boolean;
  block_reason: string | null;
  lines: GrnAssetLineSuggestion[];
}

export type MovementReportRow = {
  id: string;
  company_id: string;
  asset_id: string;
  movement_type: string;
  to_site: string | null;
  to_building: string | null;
  to_room: string | null;
  from_site: string | null;
  from_building: string | null;
  from_room: string | null;
  to_employee_id: string | null;
  from_employee_id: string | null;
  remarks: string | null;
  created_at: string;
  created_by: string | null;
  asset_code: string | null;
  asset_name: string | null;
};

export interface AssetMovementDto {
  id: string;
  company_id: string;
  asset_id: string;
  movement_type: 'issue' | 'return' | 'transfer' | 'damage';
  work_order_id?: string | null;
  work_order_stage_id?: string | null;
  from_employee_id: string | null;
  to_employee_id: string | null;
  from_department_id: string | null;
  to_department_id: string | null;
  from_warehouse_id: string | null;
  to_warehouse_id: string | null;
  from_site: string | null;
  to_site: string | null;
  from_building: string | null;
  to_building: string | null;
  from_room: string | null;
  to_room: string | null;
  remarks: string | null;
  created_by: string | null;
  created_at: string;
  is_deleted: boolean;
}

const assetService = {
  async list(params?: { q?: string; status?: string }): Promise<AssetDto[]> {
    const res = await api.get('/assets', { params });
    const data = unwrap<AssetDto[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async getById(id: string): Promise<AssetDto> {
    const res = await api.get(`/assets/${id}`);
    return unwrap<AssetDto>(res);
  },
  async create(body: Record<string, unknown>): Promise<AssetDto> {
    const res = await api.post('/assets', body);
    return unwrap<AssetDto>(res);
  },
  async update(id: string, body: Record<string, unknown>): Promise<AssetDto> {
    const res = await api.put(`/assets/${id}`, body);
    return unwrap<AssetDto>(res);
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/assets/${id}`);
  },
  async getGrnSuggestions(grnId: string): Promise<GrnAssetSuggestionsResult> {
    const res = await api.get(`/assets/grn/${grnId}/suggestions`);
    return unwrap<GrnAssetSuggestionsResult>(res);
  },
  async createFromGrn(body: {
    grn_id: string;
    grn_item_id: string;
    units: Array<{ serial_number?: string | null; asset_name?: string; remarks?: string | null }>;
  }): Promise<{ created: number; assets: AssetDto[] }> {
    const res = await api.post('/assets/from-grn', body);
    return unwrap(res);
  },
  async capitalize(id: string): Promise<{
    asset: AssetDto;
    journal_voucher_id: string;
    journal_voucher_number: string;
  }> {
    const res = await api.post(`/assets/${id}/capitalize`, {});
    return unwrap(res);
  },
  async resolveGl(id: string): Promise<import('@/services/asset-group.service').ResolvedAssetGlDto> {
    const res = await api.get(`/assets/${id}/resolved-gl`);
    return unwrap(res);
  },
  async listMovementsReport(params?: { date_from?: string; date_to?: string; movement_type?: string }): Promise<MovementReportRow[]> {
    const res = await api.get('/assets/reports/movements', { params });
    const data = unwrap<MovementReportRow[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async downloadRegisterReportAsFile() {
    const res = await api.getClient().get('/assets/reports/register.csv', { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'asset-register.csv';
    a.click();
    URL.revokeObjectURL(url);
  },
  async downloadImportTemplateAsFile() {
    const res = await api.getClient().get('/assets/import-template.csv', { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'assets-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  },
  async importFromCsv(
    file: File
  ): Promise<{ created: number; updated: number; errors: string[]; skipped: number }> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/assets/import', form);
    return unwrap(res);
  },
  async listMovements(assetId: string): Promise<AssetMovementDto[]> {
    const res = await api.get(`/assets/${assetId}/movements`);
    const data = unwrap<AssetMovementDto[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async issueMovement(
    assetId: string,
    body: Record<string, unknown>
  ): Promise<{ asset: AssetDto; movement: AssetMovementDto }> {
    const res = await api.post(`/assets/${assetId}/issue`, body);
    return unwrap(res);
  },
  async returnMovement(
    assetId: string,
    body: Record<string, unknown>
  ): Promise<{ asset: AssetDto; movement: AssetMovementDto }> {
    const res = await api.post(`/assets/${assetId}/return`, body);
    return unwrap(res);
  },
  async transferMovement(
    assetId: string,
    body: Record<string, unknown>
  ): Promise<{ asset: AssetDto; movement: AssetMovementDto }> {
    const res = await api.post(`/assets/${assetId}/transfer`, body);
    return unwrap(res);
  },
  async damageMovement(
    assetId: string,
    body: Record<string, unknown>
  ): Promise<{ asset: AssetDto; movement: AssetMovementDto }> {
    const res = await api.post(`/assets/${assetId}/damage`, body);
    return unwrap(res);
  },
  async lookupAsset(code: string): Promise<AssetDto> {
    const res = await api.get('/assets/lookup', { params: { code: code.trim() } });
    return unwrap<AssetDto>(res);
  },
  async recordVerificationScan(assetId: string): Promise<AssetDto> {
    const res = await api.post(`/assets/${assetId}/verify`, {});
    return unwrap<AssetDto>(res);
  },
  async generateBarcode(assetId: string): Promise<AssetDto> {
    const res = await api.post(`/assets/${assetId}/generate-barcode`, {});
    return unwrap<AssetDto>(res);
  },
  async generateQr(assetId: string): Promise<AssetDto> {
    const res = await api.post(`/assets/${assetId}/generate-qr`, {});
    return unwrap<AssetDto>(res);
  },
};

export default assetService;
